import { useState, useCallback } from "react";
import { useTimeoutRef } from "@/hooks/useTimeoutRef";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { cn } from "@/lib/utils";

interface FirstUseHintProps {
  id: string;
  children: React.ReactNode;
  hint: string;
  placement?: "below" | "right";
}

export function FirstUseHint({ id, children, hint, placement = "below" }: FirstUseHintProps) {
  const seen = useOnboardingStore((s) => s.seenHints[id]);
  const markHintSeen = useOnboardingStore((s) => s.markHintSeen);
  const [fading, setFading] = useState(false);
  const fadeTimerRef = useTimeoutRef();

  const handleInteraction = useCallback(() => {
    if (seen || fading) return;
    setFading(true);
    fadeTimerRef.current = setTimeout(() => markHintSeen(id), 300);
  }, [seen, fading, markHintSeen, id]);

  if (seen) return <>{children}</>;

  return (
    <div className="relative" onClickCapture={handleInteraction}>
      {children}
      <div
        className={cn(
          "absolute z-50 pointer-events-none",
          "text-[10px] text-muted-foreground/60 italic",
          "border-l-2 border-primary/30 pl-1.5",
          "animate-in fade-in-0 duration-300",
          "transition-opacity duration-300",
          fading && "opacity-0",
          placement === "below" && "left-0 top-full mt-1.5",
          placement === "right" && "left-full top-1/2 -translate-y-1/2 ml-2",
        )}
      >
        {hint}
      </div>
    </div>
  );
}
